import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import { TextInput } from '../TextInput/TextInput.esm.js';

const ERROR_MESSAGES = {
    valueMissing: "Email address is required",
    typeMismatch: "Please enter a valid email address",
    customError: "Invalid email address",
};
// Simple email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const EmailInput = forwardRef((props, ref) => {
    const { validateOn = "blur", customValidation, required = false, disabled = false, value, onChange, onBlur } = props, textInputProps = __rest(props, ["validateOn", "customValidation", "required", "disabled", "value", "onChange", "onBlur"]);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [blurAttempted, setBlurAttempted] = useState(false);
    const [inErrorState, setInErrorState] = useState(false);
    const [validationState, setValidationState] = useState({
        valid: true,
        valueMissing: false,
        typeMismatch: false,
        customError: false,
    });
    const inputRef = useRef(null);
    const validateEmail = useCallback(() => {
        const emailValue = String(value || "");
        const isValueMissing = required && !emailValue.trim();
        const isTypeMismatch = Boolean(emailValue.trim() && !EMAIL_REGEX.test(emailValue));
        let isCustomError = false;
        if (customValidation && emailValue) {
            const customError = customValidation(emailValue);
            isCustomError = !!customError;
        }
        const isValid = !isValueMissing && !isTypeMismatch && !isCustomError;
        return {
            valid: isValid,
            valueMissing: isValueMissing,
            typeMismatch: isTypeMismatch,
            customError: isCustomError,
        };
    }, [value, required, customValidation]);
    // Update validation state reactively
    // - In submit-only mode: skip reactive validation unless in error state
    // - In blur mode: skip reactive validation unless in error state
    // - When in error state: validate on every change until error is resolved
    useEffect(() => {
        // If in error state, always validate on change
        if (inErrorState) {
            const newValidationState = validateEmail();
            setValidationState(newValidationState);
            // If validation passes, exit error state and revert to validateOn mode
            if (newValidationState.valid) {
                setInErrorState(false);
            }
            return;
        }
        // Not in error state: follow normal validateOn logic
        if (validateOn === "submit")
            return; // skip reactive validation
        const newValidationState = validateEmail();
        setValidationState(newValidationState);
    }, [
        value,
        required,
        customValidation,
        blurAttempted,
        validateOn,
        validateEmail,
        inErrorState,
    ]);
    const handleBlur = (e) => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        if (validateOn === "submit")
            return; // no blur validation in submit-only mode
        setBlurAttempted(true);
        // Trigger validation after blur
        const newValidationState = validateEmail();
        setValidationState(newValidationState);
        // If validation fails, enter error state for onChange validation
        if (!newValidationState.valid) {
            setInErrorState(true);
        }
    };
    const getErrorMessage = (validity) => {
        if (validity.valid)
            return undefined;
        if (validity.valueMissing)
            return ERROR_MESSAGES.valueMissing;
        if (validity.typeMismatch)
            return ERROR_MESSAGES.typeMismatch;
        if (validity.customError)
            return ERROR_MESSAGES.customError;
        return undefined;
    };
    const shouldShowValidation = () => {
        if (validateOn === "submit")
            return submitAttempted;
        if (validateOn === "blur")
            return blurAttempted;
        return false;
    };
    const hasError = !validationState.valid && shouldShowValidation();
    const errorMessage = hasError
        ? getErrorMessage(validationState)
        : undefined;
    useEffect(() => {
        var _a;
        if (validateOn !== "submit")
            return;
        const form = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.closest("form");
        if (!form)
            return;
        const handleFormSubmit = (e) => {
            const state = validateEmail();
            setSubmitAttempted(true);
            setValidationState(state);
            // If validation fails, enter error state for onChange validation
            if (!state.valid) {
                setInErrorState(true);
                e.preventDefault();
                return;
            }
        };
        form.addEventListener("submit", handleFormSubmit);
        return () => form.removeEventListener("submit", handleFormSubmit);
    }, [validateOn, validateEmail]);
    return (jsx(TextInput, Object.assign({}, textInputProps, { type: "email", disabled: disabled, value: value, onChange: onChange, onBlur: handleBlur, error: hasError, errorMessage: errorMessage, ref: inputRef })));
});
EmailInput.displayName = "EmailInput";

export { ERROR_MESSAGES, EmailInput };
